'use client'

import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { GeneralModal, IconBox, ModalFooter } from '@/components/ui/general-modal'
import { useInviteMemberMutation } from '@/api/organization/members/invite-member-mutation'
import { toast } from '@/features/toast/toast'

interface IInviteMemberDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export const InviteMemberDialog = ({ open, onOpenChange }: IInviteMemberDialogProps) => {
    const [emailAddress, setEmailAddress] = useState('')
    const [role, setRole] = useState<'org:admin' | 'org:member'>('org:member')
    const inviteMutation = useInviteMemberMutation()

    const handleInvite = () => {
        if (!emailAddress.trim()) return

        inviteMutation.mutate(
            { emailAddress: emailAddress.trim(), role },
            {
                onSuccess: () => {
                    toast.success(`Invitation sent to ${emailAddress}`)
                    setEmailAddress('')
                    setRole('org:member')
                    onOpenChange(false)
                },
                onError: (error) => {
                    toast.error(error?.errors?.[0]?.msg || 'Failed to send invitation')
                },
            }
        )
    }

    return (
        <GeneralModal
            open={open}
            onOpenChange={onOpenChange}
            title="Invite member"
            subtitle="Send an invitation to join your workspace."
            icon={
                <IconBox>
                    <UserPlus className="h-5 w-5 text-stone-500" />
                </IconBox>
            }
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">Email address</label>
                    <Input
                        type="email"
                        placeholder="name@example.com"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleInvite()
                        }}
                        autoFocus
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">Role</label>
                    <Select value={role} onValueChange={(v) => setRole(v as 'org:admin' | 'org:member')}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="org:member">Member</SelectItem>
                            <SelectItem value="org:admin">Admin</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <ModalFooter>
                <Button variant="ds2-secondary" size="ds2" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button
                    variant="ds2-primary"
                    size="ds2"
                    onClick={handleInvite}
                    disabled={!emailAddress.trim() || inviteMutation.isPending}
                    isLoading={inviteMutation.isPending}
                >
                    Send invitation
                </Button>
            </ModalFooter>
        </GeneralModal>
    )
}
